// ============================================================
// StatementSendModal.tsx — B4: send an ISSUED settlement statement, the bills'
// way (backend settlementStatementController send / delivered):
//   • email — only to the address the client agreed to receive bills at
//     (server-enforced); the issued PDF attached; "Sent on behalf of …" when
//     the sender isn't the signatory; delivery + Activity recorded;
//   • or record it posted / handed over (date not before issue, not future);
//   • (B4 follow-up 2) a child's / protected party's statement goes to their
//     LITIGATION FRIEND (their email, THEIR consent — recorded right here);
//     none recorded → say so, post / hand instead.
// (Batch 2)
//   • statement + final bill pack: when the case has an issued final bill
//     (sendDraft.finalBill) "Also send bill … in the same email" is ticked by
//     default (both attachments listed) → send withBillId; recording a post /
//     hand delivery offers "Posted together with bill …" the same way;
//   • no email on file → To starts EMPTY; the warnings say "statement" (the
//     server's reasons talk about bills); the To error names the litigation
//     friend when it goes to them; amber notes are the shared ActionBox;
//   • the Message box starts ~8 lines and grows; unsaved-changes guard while
//     the subject / message were edited; nothing closes it while sending;
//   • a send / delivery announces caseChange('statement') (+ 'bill' for a pack).
// (Follow-ups)
//   • Send shows its real progress: "Preparing PDFs…" (the server makes both
//     PDFs ready — prepareStatementSendApi) → "Sending…" (the email); the
//     window stays open (no close, no Cancel) until it's done or refused;
//   • the pack's bill vs the costs the statement used: a warning with both
//     figures and "correct the statement" (it doesn't block the send).
// (Audit fix batch 1, 31 Oct)
//   • H2 / Low 15: the bill and the statement differ part by part → the pack
//     (email OR post / hand) waits for "I've checked the difference" + a
//     reason (MismatchCheck), sent as mismatchOverride; neutral wording;
//   • M2: a bill addressed to someone else can't go in the pack (said why, no
//     tick offered); a bill that can't be EMAILED today starts unticked;
//   • decision: "Posted / handed together with bill …" starts UNTICKED when the
//     bill already reached the client ("Bill already sent by email on …");
//     ticking it is the deliberate choice (billAlreadySent);
//   • one send key per window (idempotencyKey): a retry never sends twice;
//   • Low 9: the PDF names a different litigation friend from today's → said;
//   • the window announces nothing itself — StatementPanel's onDone does, once.
// (B4c batch 1b) A third way, "Send for e-signature" (pilot only): the
// checklist (statement/EsignSendPanel) with a fix for each missing item, then
// Send (POST …/esign/send, its own send key). Fixes that live elsewhere close
// the window and go there (onFix); "Record email consent" switches to By email.
// (E-sign audit fixes A) The second staff check — Ask a colleague to confirm /
// Confirm the details are right (POST …/esign/second-check[/approve]); Send
// waits while one is needed (a 409 SECOND_CHECK_REQUIRED shows it as it is).
// (E-sign fixes B) The Send button is honest for the whole request: a
// spinner + "Sending…", disabled and aria-busy, one press only (a second
// click while it runs does nothing); a refusal puts it back and says why (an
// e-sign refusal in an ErrorNote — the server's reason, e.g. ESIGN_OFF). The
// e-sign checklist refreshes live (hooks/useCaseLive, topic 'esign') — e.g.
// a colleague confirms the details while the window is open.
// (D12) The colleague can also NOT confirm, with a reason
// (POST …/second-check/refuse) — the sender's window shows it + Ask again.
// ============================================================
import React, { useEffect, useId, useRef, useState } from 'react'
import { approveEsignSecondCheckApi, refuseEsignSecondCheckApi, getStatementEsignApi, getStatementSendDraftApi, markStatementDeliveredApi, prepareStatementSendApi, recordLitigationFriendConsentApi, requestEsignSecondCheckApi, sendStatementApi, sendStatementForEsignApi } from '../../api/casevoApi'
import ConsentInline from '../billing/ConsentInline'
import type { CaseStatements, FixTarget, StatementSendDraft, StatementSummary } from '../../types/settlementStatement.types'
import type { EsignContactCheck, EsignFixTarget, StatementEsign } from '../../types/esign.types'
import EsignSendPanel from './statement/EsignSendPanel'
import { isOpenEsign } from './statement/EsignStatus'
import MismatchCheck, { EMPTY_MISMATCH, mismatchBlockedReason, mismatchOverrideOf, type MismatchValue } from './statement/MismatchCheck'
import { newSendKey } from '../../utils/sendKey'
import ModalFrame from '../shared/ModalFrame'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import DateInput from '../shared/DateInput'
import ActionBox, { ErrorNote } from '../shared/ActionBox'
import MessageTextarea from '../billing/MessageTextarea'
import { capacityUnknownMessage, initialSendTo, toRequiredMessage } from '../billing/SendBillModal'
import { B_PRIMARY, B_SECONDARY, BIcon, INPUT, NOTE, errorText } from '../billing/billingUi'
import { Banner } from './settlementUi'
import { isValidEmail } from '../../utils/contactFormat'
import { NO_EMAIL_ON_FILE } from '../../utils/noEmail'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { useCaseLive } from '../../hooks/useCaseLive'

interface Props {
  caseId: string
  statement: StatementSummary
  onClose: () => void
  // The fresh statements (post / hand), or null after an email (reload);
  // withBill: the final bill went too (the panel announces both, once).
  onDone: (data: CaseStatements | null, withBill: boolean) => void
  // (B4c 1b) Offer "Send for e-signature" (the firm's pilot is on), and go
  // to a fix elsewhere (Client details, the litigation friend, E-signature settings).
  esignPilot?: boolean
  onFix?: (target: FixTarget) => void
  // Open straight on e-signature (the statement card's "Send for e-signature").
  initialMode?: 'email' | 'manual' | 'esign'
}
const StatementSendModal: React.FC<Props> = ({ caseId, statement, onClose, onDone, esignPilot = false, onFix, initialMode }) => {
  const id = useId()
  const [draft, setDraft] = useState<StatementSendDraft | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [mode, setMode] = useState<'email' | 'manual' | 'esign'>(initialMode === 'esign' && esignPilot ? 'esign' : initialMode === 'manual' ? 'manual' : 'email')
  // (B4c 1b) The e-signature checklist, loaded when that way is chosen.
  const [esign, setEsign] = useState<StatementEsign | null>(null)
  const [esignErr, setEsignErr] = useState<string | null>(null)
  const [esignNonce, setEsignNonce] = useState(0)
  const [esignKey] = useState(newSendKey)
  // (E-sign audit fixes A) The second staff check (recently changed contact details).
  const [checkBusy, setCheckBusy] = useState<'ask' | 'approve' | 'refuse' | null>(null)
  const [checkErr, setCheckErr] = useState<string | null>(null)
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [ccMe, setCcMe] = useState(true)
  const [method, setMethod] = useState<'POST' | 'HAND'>('POST')
  const [date, setDate] = useState<string | null>(null)
  // (Batch 2) The final bill goes with it. (Audit fix batch 1) By email: on by
  // default unless it can't go (addressed to someone else / can't be emailed).
  // By post / hand: on by default only while the bill hasn't reached the client.
  const [withBill, setWithBill] = useState(true)
  const [withBillPost, setWithBillPost] = useState(true)
  const [mismatchOk, setMismatchOk] = useState<MismatchValue>(EMPTY_MISMATCH)
  // One key for this window's send — a retry of it never emails twice.
  const [sendKey] = useState(newSendKey)
  const [busy, setBusy] = useState(false)
  // (E-sign fixes B) One press per send: a second click before React re-renders does nothing.
  const inFlight = useRef(false)
  // (Follow-ups) Where an email send is: 'preparing' the PDFs, then 'sending'.
  const [phase, setPhase] = useState<'preparing' | 'sending' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [tried, setTried] = useState(false)
  const [nonce, setNonce] = useState(0)
  useEffect(() => {
    let live = true
    getStatementSendDraftApi(caseId, statement.id).then((d) => {
      if (!live) return
      setDraft(d); setTo(initialSendTo(d))
      if (nonce === 0) {
        const fb = d.finalBill
        const packByEmail = !!fb && !fb.refusal && !fb.emailBlockedReason
        setWithBill(packByEmail)
        setWithBillPost(!!fb && !fb.refusal && !fb.delivered)
        // The pack (ticked) has its own wording: "your bill and settlement statement".
        setSubject(packByEmail ? fb.subject ?? d.subject : d.subject); setBody(packByEmail ? fb.body ?? d.body : d.body)
        if (!d.emailAllowed && (d.noEmailAddress || d.recipient?.missing)) setMode((m) => (m === 'esign' ? m : 'manual'))
      }
    }).catch((e) => { if (live) setLoadErr(errorText(e, 'Couldn’t prepare the email.')) })
    return () => { live = false }
  }, [caseId, statement.id, nonce])
  useEffect(() => {
    if (mode !== 'esign' || !esignPilot) return undefined
    let live = true
    setEsignErr(null)
    getStatementEsignApi(caseId, statement.id).then((d) => { if (live) setEsign(d) }).catch((e) => { if (live) setEsignErr(errorText(e, 'Couldn’t check what’s needed for e-signature.')) })
    return () => { live = false }
  }, [mode, esignPilot, caseId, statement.id, esignNonce])
  // (E-sign fixes B) The checklist / second check refreshes when the case's e-sign state changes.
  useCaseLive(caseId, ['esign'], () => { if (!inFlight.current) setEsignNonce((n) => n + 1) }, { enabled: mode === 'esign' && esignPilot })
  const esignFix = (t: EsignFixTarget): void => {
    if (t === 'emailConsent') { setMode('email'); return }
    if (onFix) { onClose(); onFix(t) }
  }
  // (audit fixes A) Send waits while a colleague still has to confirm the contact details.
  const esignReady = !!esign && esign.available && esign.ready && !isOpenEsign(esign.current) && !esign.contactCheck?.needsSecondCheck
  const setContactCheck = (cc: EsignContactCheck | null | undefined): void => {
    if (cc === undefined) return
    setEsign((e) => (e && e.available ? { ...e, contactCheck: cc } : e))
  }
  const secondCheck = async (approvalId?: string): Promise<void> => {
    setCheckBusy(approvalId ? 'approve' : 'ask'); setCheckErr(null)
    try {
      setContactCheck(approvalId ? await approveEsignSecondCheckApi(caseId, statement.id, approvalId) : await requestEsignSecondCheckApi(caseId, statement.id))
    } catch (e) {
      setCheckErr(errorText(e, approvalId ? 'Couldn’t confirm the details.' : 'Couldn’t ask for a second check.'))
      setEsignNonce((n) => n + 1)
    } finally { setCheckBusy(null) }
  }
  // (D12) Don't confirm (reason required); true once recorded.
  const refuseCheck = async (approvalId: string, reason: string): Promise<boolean> => {
    setCheckBusy('refuse'); setCheckErr(null)
    try { setContactCheck(await refuseEsignSecondCheckApi(caseId, statement.id, approvalId, reason)); return true } catch (e) {
      setCheckErr(errorText(e, 'Couldn’t record that you didn’t confirm.'))
      setEsignNonce((n) => n + 1)
      return false
    } finally { setCheckBusy(null) }
  }
  // (M16) Ask before dropping an edited subject / message; never close mid-send.
  const guard = useUnsavedChangesGuard(useDraftDirty(draft !== null, [subject, body]) && !busy)
  const requestClose = (): void => { if (!busy) guard(onClose) }
  const lf = draft?.recipient?.kind === 'LITIGATION_FRIEND' ? draft.recipient : null
  const capacityUnknown = !!draft?.recipient?.capacityUnknown
  const bill = draft?.finalBill ?? null
  const billTicked = mode === 'email' ? withBill : withBillPost
  const withBillId = bill && !bill.refusal && billTicked ? bill.id : undefined
  // (H2) The pack waits for the bill check (visible next to Send).
  const packMismatch = withBillId ? mismatchBlockedReason(bill?.mismatch, bill?.mismatchOverridden, mismatchOk) : null
  // Ticking / unticking the bill swaps the wording between the pack's and the
  // statement's own — unless the user has already changed it.
  const toggleBill = (on: boolean): void => {
    setWithBill(on)
    if (!draft || !bill) return
    const from = on ? { s: draft.subject, b: draft.body } : { s: bill.subject ?? draft.subject, b: bill.body ?? draft.body }
    const to = on ? { s: bill.subject ?? draft.subject, b: bill.body ?? draft.body } : { s: draft.subject, b: draft.body }
    if (subject === from.s) setSubject(to.s)
    if (body === from.b) setBody(to.b)
  }
  const recips = to.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
  const bad = recips.find((r) => !isValidEmail(r))
  const fieldErr = {
    to: !recips.length ? toRequiredMessage(!!lf) : bad ? `“${bad}” isn’t a valid email address.` : null,
    subject: !subject.trim() ? 'Enter a subject.' : null,
    body: !body.trim() ? 'Write the covering message.' : null,
    date: mode === 'manual' && !date ? 'Enter the date it was posted or handed over.' : null,
  }
  const submit = async (): Promise<void> => {
    if (inFlight.current) return
    inFlight.current = true
    try { await doSubmit() } finally { inFlight.current = false }
  }
  const doSubmit = async (): Promise<void> => {
    setTried(true); setErr(null)
    if (mode === 'email' && (fieldErr.to || fieldErr.subject || fieldErr.body)) return
    if (mode === 'manual' && fieldErr.date) return
    if (mode === 'esign') {
      if (!esignReady) return
      setBusy(true)
      try {
        await sendStatementForEsignApi(caseId, statement.id, esignKey)
        setBusy(false)
        onDone(null, false)
      } catch (e) {
        setErr(errorText(e, 'It wasn’t sent for e-signature.')); setBusy(false)
        // (audit fixes A) 409 SECOND_CHECK_REQUIRED carries the check as it is now.
        const d = (e as { response?: { data?: { code?: string; contactCheck?: EsignContactCheck | null } } }).response?.data
        if (d?.code === 'SECOND_CHECK_REQUIRED') setContactCheck(d.contactCheck)
        else setEsignNonce((n) => n + 1)
      }
      return
    }
    if (packMismatch) return
    const override = withBillId ? mismatchOverrideOf(mismatchOk) : null
    setBusy(true)
    try {
      let fresh: CaseStatements | null = null
      if (mode === 'email') {
        setPhase('preparing')
        await prepareStatementSendApi(caseId, statement.id, withBillId ? { withBillId } : {})
        setPhase('sending')
        await sendStatementApi(caseId, statement.id, {
          to, subject, body, ccMe, idempotencyKey: sendKey, ...(withBillId ? { withBillId } : {}), ...(override ? { mismatchOverride: override } : {}),
        })
      } else {
        fresh = await markStatementDeliveredApi(caseId, statement.id, {
          method, date: date as string, ...(withBillId ? { withBillId } : {}), ...(override ? { mismatchOverride: override } : {}),
          // (Decision) The bill already reached the client: ticking it here was deliberate.
          ...(withBillId && bill?.delivered ? { billAlreadySent: true } : {}),
        })
      }
      setBusy(false); setPhase(null)
      onDone(fresh, !!withBillId)
    } catch (e) { setErr(errorText(e, mode === 'email' ? 'The statement wasn’t sent.' : 'Couldn’t record the delivery.')); setBusy(false); setPhase(null) }
  }
  const toPost = { label: 'Record it as posted or handed over', kind: 'primary' as const, onClick: () => setMode('manual') }
  return (
    <ModalFrame size="lg" onClose={requestClose} dismissible={!busy} eyebrow="Send" eyebrowIcon={<BIcon name="send" size={12} />}
      title={`Send statement ${statement.number ?? ''} v${statement.version}${lf?.name ? ` to ${lf.name} (litigation friend)` : ''}`} subtitle="The issued PDF is attached exactly as issued."
      footerRight={<><button type="button" className={B_SECONDARY} disabled={busy} onClick={requestClose}>Cancel</button>
        <button type="button" className={B_PRIMARY} data-send-button="" aria-busy={busy || undefined}
          disabled={busy || !draft || (mode === 'esign' ? !esignReady : (!!packMismatch || (mode === 'email' && (!draft.emailAllowed || capacityUnknown))))} onClick={() => { void submit() }}>
          {busy && <span className="inline-block w-[13px] h-[13px] rounded-full border-2 border-current border-t-transparent animate-spin motion-reduce:animate-none" aria-hidden="true" data-spinner="" />}
          {busy ? (mode === 'esign' ? 'Sending…' : mode === 'email' ? (phase === 'preparing' ? 'Preparing PDFs…' : 'Sending…') : 'Saving…') : mode === 'esign' ? 'Send for e-signature' : mode === 'email' ? 'Send email' : 'Record delivery'}</button></>}>
      {loadErr && <Banner tone="error">{loadErr}</Banner>}
      {!draft && !loadErr && <p className={NOTE}>Preparing the email…</p>}
      {draft && (
        <div className="flex flex-col gap-[14px]">
          <div role="radiogroup" aria-label="How it is sent" className="flex flex-wrap gap-x-[16px] gap-y-[6px] text-[13.5px] [&>label]:whitespace-nowrap">
            <label className="inline-flex items-center gap-[6px]"><input type="radio" name={`${id}-mode`} checked={mode === 'email'} disabled={busy} onChange={() => setMode('email')} />By email</label>
            <label className="inline-flex items-center gap-[6px]"><input type="radio" name={`${id}-mode`} checked={mode === 'manual'} disabled={busy} onChange={() => setMode('manual')} />Posted or handed over</label>
            {esignPilot && <label className="inline-flex items-center gap-[6px]"><input type="radio" name={`${id}-mode`} checked={mode === 'esign'} disabled={busy} onChange={() => setMode('esign')} />Send for e-signature</label>}
          </div>
          {draft.addresseeWarning && <Banner tone="warn" title="Check who this goes to">{draft.addresseeWarning}</Banner>}
          {draft.delivered && <Banner tone="info">Already {draft.delivered.method === 'EMAIL' ? 'emailed' : draft.delivered.method === 'POST' ? 'posted' : 'handed over'}. Sending again keeps the first delivery date.</Banner>}
          {mode === 'esign' ? (
            <EsignSendPanel esign={esign} loadError={esignErr} onFix={esignFix} onRetry={() => setEsignNonce((n) => n + 1)}
              checkBusy={checkBusy} checkError={checkErr} onAskSecondCheck={() => { void secondCheck() }} onApproveSecondCheck={(id) => { void secondCheck(id) }} onRefuseSecondCheck={refuseCheck} />
          ) : mode === 'email' ? (
            <>
              {lf && !lf.missing && <p className={`${NOTE} m-0`}>For a child or protected party the statement goes to their litigation friend, {lf.name}{lf.relationship ? ` (${lf.relationship})` : ''}.</p>}
              {lf?.missing && (
                <div data-no-litigation-friend><ActionBox actions={[toPost]} title="No litigation friend is recorded"
                  message={<>For a child or protected party the statement goes to their litigation friend — add them in Client details, or send the statement by post or hand it over.</>} /></div>
              )}
              {!lf?.missing && capacityUnknown && (
                <div data-capacity-unknown><ActionBox actions={[toPost]} title={capacityUnknownMessage('statement')}
                  message={<>You can still send the statement by post or hand it over.</>} /></div>
              )}
              {!lf?.missing && !capacityUnknown && !draft.emailAllowed && draft.noEmailAddress && (
                <div data-no-email><ActionBox actions={[toPost]} title={NO_EMAIL_ON_FILE}
                  message={<>Send the statement by post or hand it over, then record it here.</>} /></div>
              )}
              {/* Consent recorded right here (the client's, or the litigation friend's). */}
              {!draft.emailAllowed && !draft.noEmailAddress && !lf?.missing && !capacityUnknown && (
                <ConsentInline clientId={lf ? null : draft.clientId} clientName={lf?.name ?? undefined} partyNoun={lf ? 'the litigation friend' : undefined} documentsNoun="statements"
                  recordConsent={lf ? (b) => recordLitigationFriendConsentApi(caseId, b) : undefined}
                  to={recips} disabled={busy}
                  facts={{ consentEmail: draft.consent?.email ?? null, clientEmail: draft.clientEmail, consentChanged: draft.consentChanged }}
                  onUsePost={() => setMode('manual')} onSaved={() => setNonce((n) => n + 1)} />
              )}
              <FormField label="To" required variant="info" htmlFor={`${id}-to`}>
                <input id={`${id}-to`} className={INPUT} value={to} disabled={busy} onChange={(e) => setTo(e.target.value)} aria-invalid={(tried && !!fieldErr.to) || undefined}
                  aria-describedby={tried && fieldErr.to ? `${id}-to-err` : undefined} />
                <span id={`${id}-to-err`}>{tried && <FieldError message={fieldErr.to ?? undefined} />}</span>
              </FormField>
              <label className="inline-flex items-center gap-[6px] text-[13px]"><input type="checkbox" checked={ccMe} disabled={busy} onChange={(e) => setCcMe(e.target.checked)} />Send a copy to me ({draft.senderEmail})</label>
              <FormField label="Subject" required variant="info" htmlFor={`${id}-subject`}>
                <input id={`${id}-subject`} className={INPUT} value={subject} disabled={busy} onChange={(e) => setSubject(e.target.value)}
                  aria-invalid={(tried && !!fieldErr.subject) || undefined} aria-describedby={tried && fieldErr.subject ? `${id}-subject-err` : undefined} />
                <span id={`${id}-subject-err`}>{tried && <FieldError message={fieldErr.subject ?? undefined} />}</span>
              </FormField>
              <FormField label="Message" required variant="info" htmlFor={`${id}-body`}>
                <MessageTextarea id={`${id}-body`} className={INPUT} value={body} disabled={busy} onChange={(e) => setBody(e.target.value)}
                  aria-invalid={(tried && !!fieldErr.body) || undefined} aria-describedby={tried && fieldErr.body ? `${id}-body-err` : undefined} />
                <span id={`${id}-body-err`}>{tried && <FieldError message={fieldErr.body ?? undefined} />}</span>
              </FormField>
              {bill && !bill.refusal && (
                <div>
                  <label className="inline-flex items-center gap-[6px] text-[13px]">
                    <input type="checkbox" checked={withBill} disabled={busy || !!bill.emailBlockedReason} aria-describedby={bill.emailBlockedReason ? `${id}-bill-email` : undefined}
                      onChange={(e) => toggleBill(e.target.checked)} />Also send bill {bill.number} in the same email
                  </label>
                  {bill.emailBlockedReason && <p id={`${id}-bill-email`} className={`${NOTE} m-0 mt-[2px]`}>The bill can’t go by email: {bill.emailBlockedReason} Send the statement on its own.</p>}
                </div>
              )}
              <p className={`${NOTE} m-0`}>Attached: {draft.attachmentName}{withBillId && bill ? ` and ${bill.attachmentName}` : ''}</p>
            </>
          ) : (
            <>
              <div className="grid gap-[12px] sm:grid-cols-2">
                <FormField label="How" variant="info" htmlFor={`${id}-how`}>
                  <select id={`${id}-how`} className={INPUT} value={method} disabled={busy} onChange={(e) => setMethod(e.target.value as 'POST' | 'HAND')}>
                    <option value="POST">Posted</option><option value="HAND">Handed over</option>
                  </select>
                </FormField>
                <FormField label="Date" required variant="info">
                  <DateInput value={date} onChange={setDate} ariaLabel="Date posted or handed over" allowFuture={false} />
                  {tried && <FieldError message={fieldErr.date ?? undefined} />}
                </FormField>
              </div>
              {bill && !bill.refusal && (
                <div>
                  <label className="inline-flex items-center gap-[6px] text-[13px]">
                    <input type="checkbox" checked={withBillPost} disabled={busy} aria-describedby={bill.alreadySent ? `${id}-bill-sent` : undefined}
                      onChange={(e) => setWithBillPost(e.target.checked)} />{method === 'POST' ? 'Posted' : 'Handed over'} together with bill {bill.number}
                  </label>
                  {bill.alreadySent && (
                    <p id={`${id}-bill-sent`} className={`${NOTE} m-0 mt-[2px]`} data-bill-already-sent>
                      {bill.alreadySent}. {withBillPost ? 'This records it again in the bill’s history (its first delivery date stays).' : 'Tick only if you are sending it again with the statement.'}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          {mode !== 'esign' && bill?.refusal && <p className={`${NOTE} m-0`} data-pack-refusal>{bill.refusal}</p>}
          {mode !== 'esign' && withBillId && bill?.mismatch && (
            <div data-pack-mismatch>
              <MismatchCheck mismatch={bill.mismatch} overridden={bill.mismatchOverridden} value={mismatchOk} onChange={setMismatchOk} disabled={busy} showErrors={tried} />
            </div>
          )}
          {mode !== 'esign' && packMismatch && <p className={`${NOTE} m-0`}>{packMismatch} Or untick the bill.</p>}
          {/* (Audit fix batch 2, Low 12) Always mounted, so each step is announced. */}
          <p className={`${NOTE} m-0 flex items-center gap-[6px] empty:hidden`} role="status" data-send-progress={phase ?? (busy && mode === 'esign' ? 'esign' : '')}>
            {(phase || (busy && mode === 'esign')) && <span className="inline-block w-[12px] h-[12px] rounded-full border-2 border-casevo-brand border-t-transparent animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {phase ? `${phase === 'preparing' ? `Step 1 of 2 — preparing the PDF${withBillId ? 's' : ''}…` : 'Step 2 of 2 — sending the email…'} Please keep this window open.`
              : busy && mode === 'esign' ? 'Sending the secure link — this can take a few seconds. Please keep this window open.' : ''}
          </p>
          {err && (mode === 'esign' ? <div data-send-error=""><ErrorNote>{err}</ErrorNote></div> : <Banner tone="error">{err}</Banner>)}
        </div>
      )}
    </ModalFrame>
  )
}
export default StatementSendModal
